// Emoji safety key. A friendlier way to compare encryption keys than a
// 60-digit number: derive a short, deterministic emoji sequence from the
// two parties' identity keys. Both sides sort the keys before hashing, so
// they compute the same row of emojis and just check "do ours match?".
//
// This is the human-facing check (Telegram does the same). The full
// libsignal fingerprint stays available behind a disclosure for anyone
// who wants the maximum-entropy comparison.

import nacl from 'tweetnacl';
import { decodeUTF8 } from 'tweetnacl-util';

// A curated set of clearly-distinct, widely-rendering emoji. Avoids
// look-alikes (no near-identical faces / hearts) so a glance is enough to
// tell two rows apart.
const EMOJI = [
  '🐶','🐱','🦊','🐻','🐼','🐨','🦁','🐮','🐷','🐸',
  '🐵','🐔','🐧','🦆','🦉','🦄','🐝','🦋','🐢','🐙',
  '🦀','🐠','🐬','🐳','🦓','🦒','🐘','🦘','🐊','🦅',
  '🌵','🌲','🌴','🌷','🌻','🌼','🍀','🍁','🍄','🌍',
  '🌙','⭐','🔥','🌈','☂️','⚡','❄️','💧','🌊','🍎',
  '🍊','🍋','🍓','🍇','🍉','🍒','🥝','🥕','🌽','🍕',
  '🍔','🍟','🍿','🍩','🍪','🎂','🍫','☕','🍵','🍺',
  '🎈','🎁','🎵','🎸','🎺','🎨','🎯','🎲','🚀','✈️',
  '🚗','⚓','🏰','⏰','💡','🔑','🔔','💎','🧭','🪁',
];

/**
 * Returns a deterministic, symmetric sequence of emoji for a pair of
 * base64 identity keys. Same inputs (in any order) → same row.
 */
export function safetyEmojis(keyA_b64: string, keyB_b64: string, count = 8): string[] {
  const [a, b] = [keyA_b64, keyB_b64].sort();
  const hash = nacl.hash(decodeUTF8(a + b)); // 64 bytes (SHA-512)
  const out: string[] = [];
  // Use 2 bytes per slot for a wider spread across the emoji set.
  for (let i = 0; i < count; i++) {
    const idx = ((hash[i * 2] << 8) | hash[i * 2 + 1]) % EMOJI.length;
    out.push(EMOJI[idx]);
  }
  return out;
}
